import config from '@/config';
import { ApiBase, InternationalizedField, ListOptions } from '@/api/common';
import ApiList from '@/api/list';
import { Mutation } from '@/store';
import store from '@/store';
import { bus } from '@/bus';
import { BusEvent } from '@/models/bus';

const NO_UPLOAD_FIELDS = [
  'course_id',
  'SLUG', 
  'created', 
  'updated', 
  'logo_url',
  'mini_logo_url',
  'entry_point_course_image_url',
  'entry_point_bg_url',
  'promo_popup_course_image_url',
  'cert_bg_url',
  'cert_detail_bg_url',
  'cert_sharing_bg_url',
];

export default class Courses extends ApiBase<Courses> {
  public static readonly SLUG: string = 'courses';
  public readonly SLUG: string = 'courses';
  private static readonly URL_BASE: string = config.API_ROOT + '/courses/';

  // Initializing these fields (even with undefined)
  // is necessary, so hasOwnAttribute can find them.
  public code: string = undefined;
  public status: string | null = undefined;
  public availability: string | null = undefined;
  public name: InternationalizedField<string> = new InternationalizedField<string>();
  public name_long_i18n: InternationalizedField<string> = new InternationalizedField<string>();
  public description: InternationalizedField<string> = new InternationalizedField<string>();
  public description_long_i18n: InternationalizedField<string> = new InternationalizedField<string>();
  public logo: number | null = undefined;
  public logo_url: string | null = undefined;
  public mini_logo: number | null = undefined;
  public mini_logo_url: string | null = undefined;
  public entry_point_course_image: number | null = undefined;
  public entry_point_course_image_url: string | null = undefined;
  public entry_point_bg: number | null = undefined;
  public entry_point_bg_url: string | null = undefined;
  public promo_popup_course_image: number | null = undefined;
  public promo_popup_course_image_url: string | null = undefined;
  public promo_pop_up_newtag_enabled: boolean = undefined;
  public promo_pop_up_title_i18n: InternationalizedField<string> = new InternationalizedField<string>();
  public promo_pop_up_description_i18n: InternationalizedField<string> = new InternationalizedField<string>();
  public show_only_for_purchased_user: boolean = undefined;
  public cert_enabled: boolean = undefined;
  public cert_type: string | null = undefined;
  public cert_name_i18n: InternationalizedField<string> = new InternationalizedField<string>();
  public topics: number[] = undefined;
  public skills: number[] = undefined;
  public tracking_value: string | null = undefined;

  public cert_bg?: number | null = undefined;
  public cert_bg_url?: string | null = undefined;
  public cert_detail_bg?: number | null = undefined;
  public cert_detail_bg_url?: string | null = undefined;
  public cert_sharing_bg?: number | null = undefined;
  public cert_sharing_bg_url?: string | null = undefined;
  public created?: string = undefined;
  public updated?: string = undefined;

  public constructor() {
    super({
      urlBase: Courses.URL_BASE,
      filteredFields: NO_UPLOAD_FIELDS
    });
  }

  public assign(response: any): Courses {
    super.assign(response);

    if (response) {
      if ('name' in response) {
        this.name = new InternationalizedField<string>(response.name);
      }
      if ('name_long_i18n' in response) {
        this.name_long_i18n = new InternationalizedField<string>(response.name_long_i18n);
      }
      if ('description' in response) {
        this.description = new InternationalizedField<string>(response.description);
      }
      if ('description_long_i18n' in response) {
        this.description_long_i18n = new InternationalizedField<string>(response.description_long_i18n);
      }
      if ('promo_pop_up_title_i18n' in response) {
        this.promo_pop_up_title_i18n = new InternationalizedField<string>(response.promo_pop_up_title_i18n);
      }
      if ('promo_pop_up_description_i18n' in response) {
        this.promo_pop_up_description_i18n = new InternationalizedField<string>(response.promo_pop_up_description_i18n);
      }
      if ('cert_name_i18n' in response) {
        this.cert_name_i18n = new InternationalizedField<string>(response.cert_name_i18n);
      }
    }

    return this;
  }

  public static async get(id: number): Promise<Courses> {
    return this.doGet({
      urlBase: Courses.URL_BASE,
      id,
      create: (response) => new Courses().assign(response)
    });
  }

  public static async list(): Promise<ApiList<Courses>> {
    const options: ListOptions<Courses> = {
      urlBase: Courses.URL_BASE,
      create: (response) => new Courses().assign(response)
    };
    return this.doList(options);
  }

  public static async listAll(): Promise<ApiList<Courses>> {
    return this.doList({
      urlBase: Courses.URL_BASE,
      create: (response) => new Courses().assign(response),
      all: true,
      parameters: {
        minimal: true
      },
      readFromCache: () => store.state.courseList,
      writeToCache: async (list: ApiList<Courses>) =>
        await store.commit(Mutation.CACHE_COURSES_LIST, list)
    });
  }

  public async delete(): Promise<void> {
    const id = this.id;
    await super.delete();

    // In case the entity is cached
    await store.commit(Mutation.DELETE_FROM_COURSES_LIST, id);
    bus.$emit(BusEvent.CACHE_CHANGED);
  }

  public async save(): Promise<number> {
    const isNew = !this.id;
    const id = await super.save();

    if (isNew) {
      await store.commit(Mutation.ADD_TO_COURSES_LIST, this);
    } else {
      await store.commit(Mutation.COURSES_UPDATED, this);
    }

    bus.$emit(BusEvent.CACHE_CHANGED);

    return id;
  }

  public getName(): string {
    const name = this.name.getDefaultValue();
    if (name) {
      return name;
    }
    return '';
  }

  public getFormattedDate(): string {
    if (this.updated === undefined) {
      return 'N/A';
    }
    const index = this.updated.toLocaleString().indexOf('.');
    return this.updated.toLocaleString().substring(0, index);
  }
}
